import type { ApiError } from '@kia/shared';

export interface ApiResult<T> {
  status: number;
  ok: boolean;
  body: T | ApiError;
}

let authToken: string | null = null;
let onUnauthorized: (() => void) | null = null;

/** Set/clear the bearer token attached to every request (called by the auth provider). */
export function setAuthToken(token: string | null): void {
  authToken = token;
}

/** Current bearer token — for same-origin links that open in a new tab (`?t=` auth). */
export function getAuthToken(): string | null {
  return authToken;
}

/** Register a handler invoked when an *authenticated* request is rejected (session expired). */
export function setOnUnauthorized(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

/** The single fetch wrapper every screen uses. JSON in/out, uniform result shape, auth-aware. */
export async function api<T = unknown>(path: string, init?: RequestInit): Promise<ApiResult<T>> {
  const hasBody = init?.body != null;
  const res = await fetch(path, {
    ...init,
    // headers LAST so init's method/body carry through but its headers merge INTO
    // ours (rather than clobbering auth). Declare JSON only when we actually send a
    // body — a bodyless POST that claims application/json is rejected by Fastify
    // ("Body cannot be empty when content-type is set to 'application/json'").
    headers: {
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  // Only auto-logout when a *logged-in* request is rejected (not a failed login attempt).
  if (res.status === 401 && authToken && onUnauthorized) onUnauthorized();
  const contentType = res.headers.get('content-type') ?? '';
  const body = contentType.includes('json') ? await res.json() : await res.text();
  return { status: res.status, ok: res.ok, body: body as T | ApiError };
}

/**
 * The Persian error message from a failed api() result — the ONE place the `(res.body as { message?: string
 * }).message ?? …` cast should live (it is still copy-pasted, un-adopted, across ~31 call sites).
 *
 * The default is deliberately HONEST, not the tempting «... و در گزارش ثبت شد». The server's error handler
 * already returns a structured message for everything it sees — domain 4xx, and `server_error` (which really
 * IS logged) — so `res.body.message` is present in those cases and is shown as-is. This default fires ONLY
 * when the response is NOT our structured error at all (a Caddy 502/504, a proxy timeout, a non-JSON body):
 * the server never saw it, never wrote it to `error_log`, and there is no reference id to quote. So the
 * fallback promises only what it can keep — that something failed and a retry is worth trying. A real
 * correlation-id / reference is separate infrastructure, not a string edit (recorded in the plan).
 *
 * A call site that knows WHICH action failed should still pass its own contextual fallback
 * (`errMsg(r, 'خطا در ثبت امضا')`); that always wins and is the better message.
 */
export function errMsg(
  res: ApiResult<unknown>,
  fallback = 'خطایی رخ داد؛ لطفاً دوباره تلاش کنید.',
): string {
  return (res.body as { message?: string } | undefined)?.message ?? fallback;
}
